import { FileText, Users, Package, FolderOpen, SendHorizonal, AlertTriangle, CircleAlert, Gauge } from 'lucide-react';
import { requiredDocumentCategories } from '../../../utils/financeType';

const PRIORITY_COLORS = { high: 'border-red-500 bg-red-500/10', medium: 'border-yellow-500 bg-yellow-500/10', low: 'border-blue-500 bg-blue-500/10' };
const TAB_MAP = { applicant: 'applicants', business: 'business', asset: 'assets', document: 'documents', submission: 'submissions', task: 'tasks', consent: 'documents', income: 'income', liabilities: 'liabilities', nccp: 'nccp' };

const STATUS_STYLES = {
  Ready: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  'Mostly Ready': 'bg-blue-500/20 text-blue-300 border-blue-400/30',
  Incomplete: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  'Critical Gaps': 'bg-red-500/20 text-red-300 border-red-400/30',
};

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function toSlug(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function buildFinancialReadiness(deal) {
  const income = deal?.income || {};
  const businessIncome = income.business_income || {};
  const personalIncome = income.personal_income || {};
  const householdExpenses = income.household_expenses || {};
  const liabilities = deal?.liabilities || [];
  const docs = deal?.documents || [];
  const requiredCategories = requiredDocumentCategories(deal?.finance_type, deal?.business?.entity_type || deal?.entity_type || '');

  const missingItems = [];

  const businessRequired = [
    { key: 'annual_turnover', message: 'Business turnover missing', level: 'critical' },
    { key: 'net_profit', message: 'Net profit missing', level: 'critical' },
  ];
  const businessFilled = businessRequired.filter(item => hasValue(businessIncome[item.key])).length;
  const businessPct = Math.round((businessFilled / businessRequired.length) * 100);
  businessRequired.forEach(item => {
    if (!hasValue(businessIncome[item.key])) {
      missingItems.push({ id: item.key, text: item.message, tab: 'income', level: item.level });
    }
  });

  const personalKeys = ['salary_wages', 'rental_income', 'dividends_distributions', 'other_income', 'spouse_income'];
  const personalFilled = personalKeys.filter(key => hasValue(personalIncome[key])).length;
  const personalPct = personalFilled > 0 ? 100 : 0;
  if (!personalFilled) {
    missingItems.push({ id: 'personal-income', text: 'Personal income details missing', tab: 'income', level: 'warning' });
  }

  const householdChecks = [
    { key: 'monthly_living_expenses', message: 'Monthly living expenses missing', level: 'critical' },
    { key: 'dependants', message: 'Dependants count missing', level: 'warning' },
  ];
  const householdFilled = householdChecks.filter(item => hasValue(householdExpenses[item.key])).length;
  const householdPct = Math.round((householdFilled / householdChecks.length) * 100);
  householdChecks.forEach(item => {
    if (!hasValue(householdExpenses[item.key])) {
      missingItems.push({ id: item.key, text: item.message, tab: 'income', level: item.level });
    }
  });

  let liabilitiesPct = 0;
  if (!liabilities.length) {
    missingItems.push({ id: 'no-liabilities', text: 'No liabilities entered', tab: 'liabilities', level: 'critical' });
  } else {
    const requiredPerLiability = ['liability_type', 'lender', 'outstanding_balance', 'monthly_repayment'];
    const rowPcts = liabilities.map(liability => {
      const filled = requiredPerLiability.filter(key => hasValue(liability[key])).length;
      return Math.round((filled / requiredPerLiability.length) * 100);
    });
    liabilitiesPct = Math.round(rowPcts.reduce((sum, pct) => sum + pct, 0) / rowPcts.length);

    const hasIncompleteLiability = liabilities.some(liability => !hasValue(liability.lender) || !hasValue(liability.outstanding_balance));
    if (hasIncompleteLiability) {
      missingItems.push({ id: 'liability-fields', text: 'Some liabilities missing key details', tab: 'liabilities', level: 'warning' });
    }
  }

  const financialDocs = docs.filter(doc => requiredCategories.includes(doc.category));
  const docsAvailable = financialDocs.length > 0;
  const docsReceived = financialDocs.filter(doc => doc.is_received).length;
  const docsPct = docsAvailable ? Math.round((docsReceived / financialDocs.length) * 100) : null;

  const pendingByCategory = (category) => financialDocs.some(doc => doc.category === category && !doc.is_received);
  if (pendingByCategory('bank_statements')) {
    missingItems.push({ id: 'bank-statements', text: 'Bank statements not uploaded', tab: 'documents', level: 'warning' });
  }
  if (pendingByCategory('asset_quote')) {
    missingItems.push({ id: 'asset-quote', text: 'Asset invoice / quote not uploaded', tab: 'documents', level: 'warning' });
  }
  if (pendingByCategory('drivers_licence')) {
    missingItems.push({ id: 'id-doc', text: 'ID document not uploaded', tab: 'documents', level: 'warning' });
  }

  const sections = [
    { label: 'Business Income', pct: businessPct, tab: 'income' },
    { label: 'Personal Income', pct: personalPct, tab: 'income' },
    { label: 'Household / Living Expenses', pct: householdPct, tab: 'income' },
    { label: 'Liabilities', pct: liabilitiesPct, tab: 'liabilities' },
    { label: 'Financial Documents', pct: docsPct, tab: 'documents', optional: true },
  ];

  const weightedSections = sections.filter(section => section.pct !== null);
  const score = weightedSections.length
    ? Math.round(weightedSections.reduce((sum, section) => sum + section.pct, 0) / weightedSections.length)
    : 0;

  const hasCriticalGap = missingItems.some(item => item.level === 'critical');
  let status = 'Critical Gaps';
  if (!hasCriticalGap && score >= 85) status = 'Ready';
  else if (!hasCriticalGap && score >= 70) status = 'Mostly Ready';
  else if (!hasCriticalGap && score >= 45) status = 'Incomplete';

  return { score, status, sections, missingItems };
}

export default function OverviewTab({ deal, completeness, onTabChange }) {
  const c = completeness || {};
  const counts = c.counts || {};
  const portal = deal.portal_application;
  const financialReadiness = buildFinancialReadiness(deal);
  const tiles = [
    { label: 'Stage', value: deal.stage?.replace(/_/g, ' '), icon: FileText, color: 'text-purple-400' },
    { label: 'Finance', value: `$${(deal.amount || 0).toLocaleString()}`, sub: deal.finance_type, icon: FileText, color: 'text-green-400' },
    { label: 'Applicants', value: counts.applicants || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Assets', value: counts.assets || 0, icon: Package, color: 'text-orange-400' },
    { label: 'Documents', value: `${counts.docs_received || 0}/${counts.docs_total || 0}`, icon: FolderOpen, color: 'text-cyan-400' },
    { label: 'Submissions', value: counts.submissions || 0, icon: SendHorizonal, color: 'text-purple-400' },
    { label: 'Portal', value: (portal?.status || 'not_started').replace(/_/g, ' '), sub: portal?.last_client_activity_at ? `Last active ${new Date(portal.last_client_activity_at).toLocaleDateString()}` : 'No client activity', icon: FileText, color: 'text-indigo-300' },
  ];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="overview-tab">
      {/* Completeness meter */}
      <div className="bg-navy-800/50 rounded-xl p-5 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium">Application Readiness</h3>
          <span className={`text-sm font-semibold ${c.overall >= 75 ? 'text-green-400' : c.overall >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
            {c.overall || 0}%
          </span>
        </div>
        <div className="w-full bg-white/5 rounded-full h-2 mb-4">
          <div className={`h-2 rounded-full transition-all ${c.overall >= 75 ? 'bg-green-500' : c.overall >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${c.overall || 0}%` }} />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Business', pct: c.business_completeness },
            { label: 'Applicants', pct: c.applicant_completeness },
            { label: 'Assets', pct: c.asset_completeness },
            { label: 'Documents', pct: c.document_completeness },
          ].map(item => (
            <div key={item.label} className="text-center">
              <div className="text-xs text-gray-400 mb-1">{item.label}</div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${(item.pct||0) >= 75 ? 'bg-green-500' : (item.pct||0) >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${item.pct || 0}%` }} />
              </div>
              <div className="text-xs font-medium text-gray-300 mt-1">{item.pct || 0}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Snapshot Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(t => (
          <div key={t.label} className="bg-navy-800/50 rounded-lg p-3 border border-white/5">
            <div className="flex items-center gap-2 mb-1">
              <t.icon className={`h-3.5 w-3.5 ${t.color}`} />
              <span className="text-xs text-gray-400">{t.label}</span>
            </div>
            <div className="text-white font-semibold capitalize">{t.value}</div>
            {t.sub && <div className="text-xs text-gray-500 capitalize">{t.sub}</div>}
          </div>
        ))}
      </div>

      {/* Financial Readiness Scorecard */}
      <div className="bg-navy-800/50 rounded-xl p-5 border border-white/5 space-y-4" data-testid="financial-readiness-scorecard">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-emerald-300" />
            <h3 className="text-white font-medium" data-testid="financial-readiness-title">Application Readiness (Financials)</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-300" data-testid="financial-readiness-score">
              Financial Readiness: <span className="font-semibold text-white" data-testid="financial-readiness-score-value">{financialReadiness.score}%</span>
            </div>
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_STYLES[financialReadiness.status]}`}
              data-testid="financial-readiness-status-badge"
            >
              {financialReadiness.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="financial-readiness-sections">
          {financialReadiness.sections.map(section => {
            const sectionSlug = toSlug(section.label);
            return (
              <div key={section.label} className="bg-white/[0.03] border border-white/5 rounded-lg p-3" data-testid={`financial-section-${sectionSlug}`}>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>{section.label}</span>
                  <span data-testid={`financial-section-score-${sectionSlug}`}>
                    {section.pct === null ? 'N/A' : `${section.pct}%`}
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${(section.pct || 0) >= 75 ? 'bg-emerald-400' : (section.pct || 0) >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ width: `${section.pct || 0}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div data-testid="financial-missing-items-panel">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
            <CircleAlert className="h-3.5 w-3.5 text-amber-300" /> Missing financial items
          </h4>
          {financialReadiness.missingItems.length > 0 ? (
            <ul className="space-y-2">
              {financialReadiness.missingItems.slice(0, 10).map(item => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 text-sm bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2"
                  data-testid={`financial-missing-item-${item.id}`}
                >
                  <span className="text-gray-300">{item.text}</span>
                  <button
                    onClick={() => onTabChange(item.tab)}
                    className="text-xs text-purple-300 hover:text-purple-200"
                    data-testid={`financial-go-to-${item.id}`}
                  >
                    Go to section
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-emerald-300" data-testid="financial-missing-items-empty">No financial gaps detected.</div>
          )}
        </div>
      </div>

      {/* Next Actions */}
      {c.next_actions?.length > 0 && (
        <div className="bg-navy-800/50 rounded-xl p-5 border border-white/5">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400" /> Next Actions
          </h3>
          <div className="space-y-2">
            {c.next_actions.map((action, i) => (
              <button key={i} onClick={() => onTabChange(TAB_MAP[action.type] || 'overview')}
                className={`w-full text-left px-3 py-2.5 rounded-lg border-l-3 text-sm text-gray-300 hover:bg-white/5 transition ${PRIORITY_COLORS[action.priority] || PRIORITY_COLORS.medium}`}>
                {action.message}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* File Summary One-Pager */}
      <div className="bg-navy-800/50 rounded-xl p-5 border border-white/5">
        <h3 className="text-white font-medium mb-3">File Summary</h3>
        <div className="text-sm text-gray-300 space-y-2">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1">
            <div><span className="text-gray-500">Client:</span> {deal.client_name}</div>
            <div><span className="text-gray-500">Business:</span> {deal.business_name || '—'}</div>
            <div><span className="text-gray-500">ABN:</span> {deal.abn || '—'}</div>
            <div><span className="text-gray-500">Entity:</span> {deal.entity_type || '—'}</div>
            <div><span className="text-gray-500">Industry:</span> {deal.industry || '—'}</div>
            <div><span className="text-gray-500">Years Trading:</span> {deal.years_trading || '—'}</div>
            <div><span className="text-gray-500">Finance Type:</span> {deal.finance_type}</div>
            <div><span className="text-gray-500">Amount:</span> ${(deal.amount||0).toLocaleString()}</div>
            <div><span className="text-gray-500">Term:</span> {deal.term_months || 60} months</div>
            <div><span className="text-gray-500">Asset:</span> {deal.asset_description || '—'}</div>
          </div>
          {deal.ai_recommendation && (
            <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <div className="text-xs text-purple-400 font-medium mb-1">AI Recommendation</div>
              <div className="text-xs text-gray-400 line-clamp-3">{deal.ai_recommendation}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
